import { useMemo, useState } from "react";
import SectionHeading from "./SectionHeading";
import { heroImages, trackingEvents } from "@/mocks/siteData";

const featuredImgs = [heroImages.cardFollow, heroImages.cardFlip, heroImages.cardMic];
const featuredCaptions = ["后续更新中", "反转进行中", "回应跟踪中"];

export default function Tracking() {
  const [category, setCategory] = useState("全部");
  const [query, setQuery] = useState("");

  const categories = useMemo(() => {
    const set = new Set(trackingEvents.map((e) => e.category));
    return ["全部", ...Array.from(set)];
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return trackingEvents.filter(
      (e) =>
        (category === "全部" || e.category === category) &&
        (!q || (e.name + e.category).toLowerCase().includes(q))
    );
  }, [category, query]);

  const featured = filtered.slice(0, 3);
  const rest = filtered.slice(3);

  const goTimeline = () => {
    const el = document.getElementById("timeline");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="tracking" className="scroll-mt-20 bg-background-100">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHeading
            kicker="Now Tracking"
            title="正在追踪 · 38 件事在跟进"
            subtitle="这些事件都在持续跟进中，状态实时标记。挑一个感兴趣的，去时间线把它从头到尾补完。"
          />
          <label className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 focus-within:border-primary-400">
            <i className="ri-search-line text-foreground-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索追踪中的事件…"
              aria-label="搜索追踪事件"
              className="w-44 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 md:w-56"
            />
          </label>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                category === c
                  ? "border-background-950 bg-background-950 text-background-50"
                  : "border-background-200 bg-background-50 text-foreground-600 hover:border-background-300 hover:text-foreground-900"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {featured.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            {featured.map((ev, idx) => (
              <article key={ev.id} className="group overflow-hidden rounded-2xl border border-background-200 bg-background-50">
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={featuredImgs[idx % featuredImgs.length]}
                    alt={`${ev.name}的${featuredCaptions[idx % featuredCaptions.length]}主题插画`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-background-950/80 px-2.5 py-1 text-[11px] font-bold text-background-50 backdrop-blur">
                    <i className="ri-fire-line text-primary-500" />
                    {ev.hot}
                  </span>
                  <span className="absolute right-3 top-3 rounded-md bg-accent-500 px-2 py-0.5 text-[11px] font-bold text-background-50">
                    {featuredCaptions[idx % featuredCaptions.length]}
                  </span>
                </div>
                <div className="flex flex-col p-4">
                  <p className="text-xs font-medium text-secondary-700">{ev.category}</p>
                  <h3 className="mt-1 font-heading text-base font-bold leading-snug text-foreground-950">{ev.name}</h3>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-foreground-600">
                    <i className="ri-time-line" />
                    最近更新：{ev.lastUpdate}
                  </p>
                  <button
                    type="button"
                    onClick={goTimeline}
                    className="mt-3 inline-flex w-max items-center gap-1.5 whitespace-nowrap text-sm font-bold text-primary-600 hover:text-primary-700"
                  >
                    查看时间线
                    <i className="ri-arrow-right-line" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : null}

        {rest.length > 0 ? (
          <div className="mt-5 divide-y divide-background-200 overflow-hidden rounded-2xl border border-background-200 bg-background-50">
            {rest.map((ev) => (
              <button
                key={ev.id}
                type="button"
                onClick={goTimeline}
                className="flex w-full flex-col gap-2 px-4 py-4 text-left transition-colors hover:bg-background-100 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-start gap-3 sm:items-center">
                  <span className="relative mt-1 inline-flex h-2.5 w-2.5 shrink-0 sm:mt-0">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary-500 opacity-60" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-secondary-500" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground-900">{ev.name}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">
                      {ev.category} · {ev.status}
                    </p>
                  </div>
                </div>
                <span className="ml-5 inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-foreground-500 sm:ml-0">
                  <i className="ri-time-line" />
                  {ev.lastUpdate}
                  <i className="ri-arrow-right-s-line ml-1 text-primary-500" />
                </span>
              </button>
            ))}
          </div>
        ) : null}

        {filtered.length === 0 ? (
          <p className="mt-6 rounded-2xl border border-dashed border-background-300 bg-background-50 px-5 py-10 text-center text-sm text-foreground-500">
            没有符合条件的事件，换个分类或关键词试试。
          </p>
        ) : null}

        <p className="mt-5 text-center text-xs text-foreground-500">
          共 {filtered.length} 个符合条件的事件 · 每个事件完结后会从追踪列表移出并归档
        </p>
      </div>
    </section>
  );
}